import { ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { YarnQcProxyService } from '../../../proxies/inventory/yarn-qc.proxy.service';
import { YarnQcSearch } from '../../../models/yarn/yarn-qc-search';
import { YarnQC } from '../../../models/yarn/yarn-qc';
import { InvoiceProxyService } from '../../../proxies/order/invoice.proxy.service';
import { Invoice, InvoiceSearchCommand } from '../../../models/invoice/invoice';
import { ShipmentProxyService } from '../../../proxies/order/shipment.proxy.service';
import { Shipment } from '../../../models/order/shipment';

import { FinanceProxyService } from '../../../proxies/finance/finance.proxy.service';
import { Business, BusinessSearch } from '../../../models/finance/business';
import { ItemMaster } from '../../../models/item/item-master';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { finalize, forkJoin, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-yarns-qc',
  templateUrl: './yarns-qc.component.html',
  styleUrl: './yarns-qc.component.scss',
})
export class YarnsQcComponent implements OnInit {
  public views = [
    { label: 'Quality Control', value: 1 },
    { label: 'Invoices Pending', value: 2 },
  ];
  isLoading = 0;

  yarnsQC = signal<YarnQC[]>([]);
  allInvoices = signal<Invoice[]>([]);
  allShipments = signal<Shipment[]>([]);
  providers = signal<Business[]>([]);
  allItemMasters = signal<ItemMaster[]>([]);

  selectedView = 1;

  constructor(
    private router: Router,
    private yarnQCProxyService: YarnQcProxyService,
    private invoiceProxyService: InvoiceProxyService,
    private shipmentProxyService: ShipmentProxyService,
    private financeProxyService: FinanceProxyService,
    private itemMasterProxyService: ItemMasterProxyService,
    private ctr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  redirectToDetail(id: string) {
    this.router.navigate(['/yarn-qc-details'], { queryParams: { id } });
  }

  private loadInitialData() {
    this.isLoading++;
    const invoiceCommand: InvoiceSearchCommand = {
      invoiceNumber: '',
      excludeInvoicesInShipment: false,
    };
    const businessSearch: BusinessSearch = {
      name: '',
      code: '',
      businessTypes: [0],
    };

    forkJoin({
      invoices: this.invoiceProxyService.searchInvoice(invoiceCommand, 1, 9999),
      shipments: this.shipmentProxyService.getShipments(),
      providers: this.financeProxyService.getAllProviders(businessSearch),
    })
      .pipe(
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe({
        next: (results) => {
          this.allInvoices.set(results.invoices.data?.items ?? []);
          this.allShipments.set(results.shipments.data ?? []);
          this.providers.set(results.providers.data ?? []);

          this.getYarnsQCAndLots();
        },
        error: (error) => {
          console.error('Error fetching initial data:', error);
        },
      });
  }

  private getYarnsQCAndLots() {
    this.isLoading++;
    const yarnQcSearch: YarnQcSearch = {
      invoiceReference: '',
      manufacturerLotNumber: '',
      itemCode: '',
    };

    this.yarnQCProxyService
      .getYarnsQC(yarnQcSearch)
      .pipe(
        switchMap(({ data }) => {
          if (data && data.length > 0) {
            this.yarnsQC.set(data);
            const itemLots: string[] = data
              .flatMap((yarn) => yarn.itemCodeKeyWithResults ?? [])
              .map((item) => item?.itemCode)
              .filter((code): code is string => !!code);

            if (itemLots.length > 0) {
              return this.itemMasterProxyService.getByItemCodes(itemLots);
            }
          }
          return of({ data: [] });
        }),
        finalize(() => {
          this.isLoading--;
        }),
      )
      .subscribe({
        next: ({ data }) => {
          if (data) {
            this.allItemMasters.set(data);
            this.ctr.detectChanges();
          }
        },
        error: (error) => {
          console.error('Error fetching lots:', error);
        },
      });
  }

  getInvoiceName(id: string): string {
    return this.allInvoices().find((invoice) => invoice.id === id)?.invoiceNumber ?? '----';
  }

  getShipmentByInvoiceReference(invoiceReference: string): string {
    const shipment = this.allShipments().find((shipment) => shipment.invoiceReferences.find((invRef) => invRef === invoiceReference));

    return shipment ? shipment.shipmentNumber! : '----';
  }

  getProvider(id: string): string {
    const idProvider = this.allInvoices().find((invoice) => invoice.id === id)?.purchaseOrder?.providerReference;

    return this.providers().find((provider) => provider.id === idProvider)?.name ?? '----';
  }

  getYarnByItemMaster(itemCode: string): string {
    const yarn = this.allItemMasters().find((itemMaster) => itemMaster.itemCode === itemCode)?.baseYarnCode ?? '----';

    return yarn;
  }
}
